// Dynamic font loading for subscriber-facing pages.
// Only loads fonts that the newsletter actually uses based on theme_configuration.

const fontLoaders = {
  Aleo: async () => {
    await import("@fontsource/aleo/400.css");
    await import("@fontsource/aleo/400-italic.css");
    await import("@fontsource/aleo/700.css");
    await import("@fontsource/aleo/700-italic.css");
  },
  Alegreya: async () => {
    await import("@fontsource-variable/alegreya");
    await import("@fontsource-variable/alegreya/wght-italic.css");
  },
  "Cabin Variable": async () => {
    await import("@fontsource-variable/cabin");
    await import("@fontsource-variable/cabin/wght-italic.css");
  },
  "EB Garamond": async () => {
    await import("@fontsource-variable/eb-garamond");
    await import("@fontsource-variable/eb-garamond/wght-italic.css");
  },
  "Figtree Variable": async () => {
    await import("@fontsource-variable/figtree");
    await import("@fontsource-variable/figtree/wght-italic.css");
  },
  "IBM Plex Mono": async () => {
    await import("@fontsource/ibm-plex-mono/400.css");
    await import("@fontsource/ibm-plex-mono/400-italic.css");
    await import("@fontsource/ibm-plex-mono/500.css");
    await import("@fontsource/ibm-plex-mono/600.css");
    await import("@fontsource/ibm-plex-mono/700.css");
    await import("@fontsource/ibm-plex-mono/700-italic.css");
  },
  Lato: async () => {
    await import("@fontsource/lato/400.css");
    await import("@fontsource/lato/400-italic.css");
    await import("@fontsource/lato/700.css");
    await import("@fontsource/lato/700-italic.css");
  },
  Lora: async () => {
    await import("@fontsource-variable/lora");
    await import("@fontsource-variable/lora/wght-italic.css");
  },
  "Merriweather Variable": async () => {
    await import("@fontsource-variable/merriweather");
    await import("@fontsource-variable/merriweather/wght-italic.css");
  },
  "Montserrat Variable": async () => {
    await import("@fontsource-variable/montserrat");
    await import("@fontsource-variable/montserrat/wght-italic.css");
  },
  "Source Serif 4": async () => {
    await import("@fontsource-variable/source-serif-4");
    await import("@fontsource-variable/source-serif-4/wght-italic.css");
  },
};

// Load fonts based on names. Skips system fonts and unknown fonts.
async function loadFonts(fontNames) {
  const uniqueFonts = [...new Set(fontNames.filter(Boolean))];
  await Promise.all(
    uniqueFonts.map((name) => {
      const loader = fontLoaders[name];
      if (loader) {
        return loader();
      }
      return Promise.resolve();
    })
  );
}

// Extract font names from CSS custom properties set on :root
// The template renders theme_configuration as --font-body and --font-accent
function getFontsFromCssVariables() {
  const styles = getComputedStyle(document.documentElement);
  const fonts = [];

  const fontBody = styles.getPropertyValue("--font-body").trim();
  const fontAccent = styles.getPropertyValue("--font-accent").trim();

  if (fontBody) fonts.push(fontBody);
  if (fontAccent) fonts.push(fontAccent);

  return fonts;
}

// Load fonts on page load
const fonts = getFontsFromCssVariables();
if (fonts.length > 0) {
  loadFonts(fonts);
}

// Re-check fonts after preview DOM morphing (the preview iframe sends this
// message whenever nanomorph patches the document instead of doing a full
// reload, which means fonts.js doesn't re-execute but CSS variables may have
// changed to reference different fonts).
window.addEventListener("message", (event) => {
  if (event.data === "preview:morphed") {
    const updated = getFontsFromCssVariables();
    if (updated.length > 0) {
      loadFonts(updated);
    }
  }
});
